import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import AddMultipleTransactions from "./AddMultipleTransactions";
import { useForm, Controller } from "react-hook-form";
import {
  isNumber,
  overChargingTransaction,
  transactionAlreadyExists,
  validateDateFormat,
} from "../../util/validators";
import {
  GET_CARD_WITH_HISTORY_FOR_PERSON,
  GET_EXTENDED_CARD_FOR_PERSON,
} from "../../redux/reducers/cardReducer";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { MODAL_CONFIRM } from "../../redux/reducers/modalReducer";
import { RegisterTransactionDTO, User } from "../../generated/api";
import { RESET_WARN_MESSAGES } from "../../redux/reducers/messageReducer";
import { REGISTER_TRANSACTION } from "../../redux/reducers/transactionReducer";
import { isPrivileged } from "../../util/PrivilegeUtil";
import { RESET_HSA } from "../../redux/reducers/hsaReducer";
import { addStaticTimeToDate } from "../../util/formats";

export function AddTransaction() {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cardTypes = useSelector((state: any) => state.cardTypes.cardTypes);
  const searchCardForm = useSelector((state: any) => state.card.params);
  const extendedCard = useSelector((state: any) => state.card.extendedCard);
  const carePrincipalId = useSelector(
    (state: any) => state.session.selectedCarePrincipal.id
  );
  const currentUser: User = useSelector(
    (state: any) => state.session.currentUser
  );

  const [warningMessages, setWarningMessages] = useState<string[]>([]);

  const [checkSwitch, setCheckSwitch] = useState(false);
  const [dateValue, setDateValue] = useState<string>(
    new Date().toLocaleDateString()
  );
  const [dateValueError, setDateValueError] = useState(false);
  const [ignoreWarning, setIgnoreWarning] = useState(false);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWarningMessages([]);
    setCheckSwitch(event.target.checked);
    dispatch({ type: RESET_HSA });
  };

  useEffect(() => {
    const params = searchCardForm;
    setWarningMessages([]);
    dispatch({ type: GET_EXTENDED_CARD_FOR_PERSON, params });
  }, [dispatch, searchCardForm]);

  function validate(transaction: RegisterTransactionDTO) {
    let warn = false;
    let warnings: string[] = [];
    if (overChargingTransaction(extendedCard, transaction)) {
      warn = true;
      warnings = [...warnings, t("warn.overcharged")];
    }
    if (transactionAlreadyExists(extendedCard, transaction)) {
      warn = true;
      warnings = [
        ...warnings,
        t("warn.existing", { amount: transaction.amount }),
      ];
    }
    setWarningMessages(warnings);
    return warn;
  }

  const onSubmit = async (values: any) => {
    if (!dateValueError) {
      const transactionDate = new Date(dateValue);
      const registerTransaction: RegisterTransactionDTO = {
        carePrincipalId: carePrincipalId,
        cardTypeId: searchCardForm.cardTypeId,
        transactionDate: addStaticTimeToDate(transactionDate),
        amount: values.amount,
        personId: searchCardForm.personId,
      };

      const strippedId = searchCardForm.personId.replace("-", "");
      const params = {
        personId: strippedId,
        cardTypeId: searchCardForm.cardTypeId,
        carePrincipalId: carePrincipalId,
        ignoreWarning: ignoreWarning,
      };

      const cardType = cardTypes.find(
        (o: any) => o.id === searchCardForm.cardTypeId
      );

      if (!validate(registerTransaction) || ignoreWarning) {
        // eslint-disable-next-line
        dispatch({
          type: REGISTER_TRANSACTION,
          registerTransaction,
          cardType,
        });

        if (warningMessages.length === 0 || ignoreWarning) {
          dispatch({ type: GET_CARD_WITH_HISTORY_FOR_PERSON, params });
          dispatch({ type: RESET_WARN_MESSAGES });
          dispatch({ type: MODAL_CONFIRM });
        }
      }
    }
  };

  function handleChangeWarning(event: any) {
    setIgnoreWarning(event.target.checked);
  }

  const handleDateValueChange = (date: any) => {
    if (date != null && validateDateFormat(date)) {
      setDateValue(date);
      setDateValueError(false);
    } else {
      setDateValue(date);
      setDateValueError(true);
    }
  };

  return (
    <>
      {warningMessages?.map((message: string, i: number) => (
        <Alert sx={{ mt: 0.5 }} key={i} severity="error">
          {message}
        </Alert>
      ))}
      {isPrivileged(
        currentUser.privileges,
        "EFK.TRANSACTION.ADD.RETROACTIVE",
        "WRITE"
      ) ? (
        <FormGroup sx={{ maxWidth: 300 }}>
          <FormControlLabel
            control={
              <Switch checked={checkSwitch} onChange={handleSwitchChange} />
            }
            label={t<string>("searchCard.manageTxns.addMultipleTransactions")}
          />
        </FormGroup>
      ) : null}
      {checkSwitch ? (
        <AddMultipleTransactions />
      ) : (
        <Stack direction="column" alignItems="flex-start" spacing={3}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="column" alignItems="normal" spacing={2}>
              <Controller
                name="amount"
                control={control}
                render={() => (
                  <TextField
                    sx={{ mt: "10px" }}
                    error={errors.amount ? true : false}
                    id="amount"
                    label={t("amount") + " *"}
                    {...register("amount", {
                      required: true,
                      validate: isNumber,
                    })}
                  />
                )}
              />
              {errors.amount && errors.amount.type === "required" && (
                <Typography
                  variant="body2"
                  sx={{ color: "#DC143C" }}
                  role="alert"
                >
                  {t("errors.emptyField")}
                </Typography>
              )}
              {errors.amount && errors.amount.type === "validate" && (
                <Typography
                  variant="body2"
                  sx={{ color: "#DC143C" }}
                  role="alert"
                >
                  {t("errors.onlyNumbers")}
                </Typography>
              )}
              <Stack direction={"column"}>
                <LocalizationProvider
                  adapterLocale={sv}
                  dateAdapter={AdapterDateFns}
                >
                  <DatePicker
                    label={t("date")}
                    mask=""
                    value={dateValue}
                    onChange={handleDateValueChange}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: t("dateFormat"),
                        }}
                        error={dateValueError}
                      />
                    )}
                  />
                </LocalizationProvider>
                {dateValueError ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "#DC143C" }}
                    role="alert"
                  >
                    {t("errors.invalidDate")}
                  </Typography>
                ) : null}
              </Stack>
              {warningMessages.length > 0 ? (
                <FormControlLabel
                  label="Godkänn"
                  control={
                    <Checkbox
                      checked={ignoreWarning}
                      onChange={handleChangeWarning}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                />
              ) : null}
              <DialogActions>
                <Button
                  variant="contained"
                  onClick={() => dispatch({ type: "MODAL_CANCEL" })}
                >
                  {t("cancel")}
                </Button>

                <Button
                  variant="contained"
                  color="success"
                  disabled={!ignoreWarning && warningMessages.length > 0}
                  type="submit"
                >
                  {t("searchCard.manageTxns.addTransaction")}
                </Button>
              </DialogActions>
            </Stack>
          </form>
        </Stack>
      )}
    </>
  );
}
